import { usePermissions as useMaterialPermissions } from '../../../hooks/usePermissions';
import MaterialTransitionFields from '../../components/MaterialTransitionFields';
import { materialTransitionPayload } from '../../utils/materialTransitionPayload';
import { useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import { TRANSITION_TEXTS } from '../../constants/materialValuationTexts';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomTextArea,
} from '../../../common/components';
import { useReverseConsumption } from '../../../hooks/useConsumptions';
import {
  INITIAL_REVERSE_CONSUMPTION_STATE,
  REVERSE_CONSUMPTION_TEXTS,
} from '../constants/consumptionsConstants';
import {
  buildReversePayload,
  getConsumptionApiErrorMessage,
  validateReverseConsumptionForm,
} from '../utils/consumptionsUtils';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose } from '../../utils/actionLockUtils';

const ReverseConsumptionDialog = ({
  open,
  onClose,
  consumption,
  onSuccess,
  onPendingChange,
}) => {
  const { permissions: materialPermissions } = useMaterialPermissions();
  const [valuation, setValuation] = useState({});
  const [formData, setFormData] = useState(INITIAL_REVERSE_CONSUMPTION_STATE);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const reverseMutation = useReverseConsumption();
  useReportPendingState(reverseMutation.isPending, onPendingChange);
  const isLocked = reverseMutation.isPending;

  useEffect(() => {
    if (open) {
      setValuation({});
      setFormData(INITIAL_REVERSE_CONSUMPTION_STATE);
      setErrors({});
      setErrorMessage('');
    }
  }, [open, consumption?.uuid]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateReverseConsumptionForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!consumption?.uuid) return;

    try {
      setErrorMessage('');
      const valuations = Object.fromEntries(
        Object.entries(valuation)
          .filter(([, value]) => materialTransitionPayload(value))
          .map(([key, value]) => [key, materialTransitionPayload(value)])
      );
      const payload = { ...buildReversePayload(formData), valuations };
      const updatedConsumption = await reverseMutation.mutateAsync({
        id: consumption.uuid,
        payload,
      });
      onSuccess?.(updatedConsumption);
      onClose();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo revertir el consumo')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isLocked, onClose)}
      size="medium"
    >
      <DialogTitle>Revertir consumo</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Consumo"
          name="consumptionNumber"
          value={consumption?.consumption_number || '-'}
          disabled
        />

        <CustomTextArea
          label={REVERSE_CONSUMPTION_TEXTS.reason}
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          required
          disabled={isLocked}
          minRows={2}
          error={Boolean(errors.reason)}
          helperText={errors.reason}
        />

        {materialPermissions.canManageMaterialValuation
          && (consumption?.items || [])
            .filter((item) => item.requires_transition_valuation)
            .map((item) => (
              <MaterialTransitionFields
                key={item.uuid}
                title={TRANSITION_TEXTS.itemTitle(
                  item.article_name || TRANSITION_TEXTS.noArticle,
                  item.quantity
                )}
                value={valuation[item.uuid] || {}}
                onChange={(value) => setValuation((prev) => ({ ...prev, [item.uuid]: value }))}
                disabled={isLocked}
              />
            ))}
        <CustomTextArea
          label="Observaciones"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          disabled={isLocked}
        />
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel="Revertir consumo"
        saveButtonProps={{ isLoading: reverseMutation.isPending }}
      />
    </DialogContainer>
  );
};

export default ReverseConsumptionDialog;
